// src/features/leases/pages/components/LeasePortfolioFilters.jsx
//
// LSE-LEASES-LANDING-TOOLBAR-ALIGN — visual component removed; the
// unified toolbar in LeasesPage owns all FilterDropdown chips,
// openDropdown state, pipe dividers, and Clear button inline (matching
// PortfolioView canonical pattern).
//
// LSE-LEASES-LANDING-TOOLBAR-ALIGN-FOLLOWUP: filter set updated:
//   removed  — status (ShowExpiredToggle handles expired visibility)
//   added    — stage  (filters by lease's LOCATION stage)
//   added    — mAndA  (filters by lease's LOCATION M&A tag)
//
// LSE-LEASES-LANDING-TOOLBAR-ALIGN-FOLLOWUP-2 (C1):
//   removed  — showExpired parameter and pre-filter block entirely.
//              The Stage dropdown (Vacated / Retired options) now
//              serves as the mechanism to exclude non-active-stage
//              locations. Expired leases continue to render as gray
//              bars in the Gantt; the pre-filter exclusion is gone.
//
// LSE-LEASES-LANDING-STATUS-FILTER-REPLACE-STAGE:
//   Stage filter fully replaced by Status filter.
//   Stage compared locationStage; Status compares derived lease status
//   (see statusFor helper below) — a different conceptual dimension.
//   Values: draft | pre_commencement | in_use | vacant_held | expired | all.
//   Locations Stage filter is unchanged and intentionally divergent
//   (feedback_enum_value_inherits_group_behavior).
//
// This file retains only the pure logic exports consumed by
// LeasesPage (DEFAULT_FILTERS, applyFilters, statusFor) and
// tested by leasePortfolioFilters.test.js.
//

// Shared empty-state copy — single source of truth used by both the List
// (LeasesListTable) and Timeline (LeasesPage Gantt) views so the
// wording stays in sync when copy-edited in future.
//
// LEASES_EMPTY_STATE_MESSAGE — shown when filters are active but yield 0 results.
// LEASES_TRULY_EMPTY_MESSAGE — shown when the portfolio has no leases at all
//   (filters are irrelevant; "Clear filters" button must NOT appear alongside this).
export const LEASES_EMPTY_STATE_MESSAGE = "No leases match your current filters.";
export const LEASES_TRULY_EMPTY_MESSAGE = "No leases have been added yet.";

// Filter key order mirrors the toolbar left-to-right order:
// Status │ Region │ Lease │ M&A │ [Focus │ Horizon]
// focus and horizon are Action-view display preferences; they persist in the
// same filter object so URL sharing and session storage cover them automatically.
export const DEFAULT_FILTERS = {
  status:    "all_active", // In-Use + Vacant-Held only, excluding Committed/Draft/Expired.
  region:    "all",
  leaseType: "all",
  mAndA:     "all",
  focus:     "24mo",       // Action view focus window (LEASES-ACTION-VIEW S1)
  horizon:   "full",       // Action view horizon (LEASES-ACTION-VIEW S1)
};

// statusFor — maps a single lease to its Status filter bucket.
//
// Precedence (applied in order; first match wins):
//   0. leaseStatus        — an explicit user selection is authoritative in every
//                           display surface, including terminal/type-derived rows.
//   1. draft              — lease.status === 'draft' (legacy fallback)
//   2. expired            — lease.status === 'expired' (legacy fallback)
//   3. sublease_out       — active AND lease type is sublease_outbound
//   4. vacant_held        — active AND location.stage === 'vacated' (leased, empty, not sublet)
//   5. pre_commencement   — LEASES-OUTLOOK-DATA-FIXES: active AND startDate is in the future
//                           (today-exclusive). Lease has been signed but not yet commenced.
//   6. in_use             — active, Ripple occupying the space
//
// `now` is an optional stable date used by tests to avoid time-sensitive assertions.
// Production callers omit it (defaults to Date.now).
export function statusFor(lease, now = new Date()) {
  if (lease.leaseStatus != null) return lease.leaseStatus === "sublease_out" ? "vacant_held" : lease.leaseStatus;
  if (lease.status === "draft")   return "draft";
  if (lease.status === "committed") return "pre_commencement";
  if (lease.status === "expired" || lease.status === "terminated") return "expired";
  if (lease.status === "sublease_out") return "vacant_held";
  if (lease.locationStage === "vacated") return "vacant_held";
  // Pre-commencement: active lease whose start date is strictly in the future.
  if (lease.startDate) {
    const start = new Date(String(lease.startDate).slice(0, 10) + "T00:00:00");
    if (!isNaN(start.getTime()) && start > now) return "pre_commencement";
  }
  return "in_use";
}

export function isActiveOccupancy(lease, now) {
  return ["in_use", "vacant_held"].includes(statusFor(lease, now));
}

export function isActiveOrCommitted(lease, now) {
  return isActiveOccupancy(lease, now) || statusFor(lease, now) === "pre_commencement";
}

// applyFilters — pure filter predicate over a lease array.
//
// Status filter: uses statusFor(lease) — see precedence rule above.
//   "all_active"   — meta-value: include all non-expired, non-draft leases (default).
//                    Mirrors Locations ShowClosedToggle default-exclude pattern.
//   "all_statuses" — show-everything meta-value: skip status gate entirely.
//                    Mirrors Locations "all_stages" (LSE-LEASES-STATUS-DROPDOWN-PARITY-WITH-LOCATIONS).
//   "in_use" / "vacant_held" / "sublease_out" / "expired" / "draft" — exact bucket match.
//   "all"    — legacy no-op; kept as safe fallback for any persisted old state.
// Region filter: compares against l.region (from location join).
// Lease type filter: compares against l.leaseType (or l.type).
// M&A filter: compares against l.locationMAndA.
//   "any"  — include any lease whose location has an M&A tag (non-null).
//   "none" — include only leases whose location has no M&A tag (null).
//   <id>   — exact match against l.locationMAndA.
export function applyFilters(leases, filters, now = new Date()) {
  return leases.filter((l) => {
    // Status filter (derived lease status — see statusFor)
    if (filters.status === "all_statuses") {
      // Show-everything meta-value: skip status gate entirely (all leases pass).
    } else if (filters.status === "all_active") {
      if (!isActiveOccupancy(l, now)) return false;
    } else if (filters.status !== "all") {
      // Specific bucket (in_use / vacant_held / sublease_out / expired / pre_commencement)
      if (statusFor(l, now) !== filters.status) return false;
    }

    // Region filter (location region)
    if (filters.region !== "all" && l.region !== filters.region) return false;

    // Lease type filter
    if (filters.leaseType !== "all" && (l.leaseType || l.type) !== filters.leaseType) return false;

    // M&A filter (location M&A tag)
    if (filters.mAndA !== "all") {
      if (filters.mAndA === "any"  && l.locationMAndA == null)  return false;
      if (filters.mAndA === "none" && l.locationMAndA != null)  return false;
      if (filters.mAndA !== "any" && filters.mAndA !== "none" && l.locationMAndA !== filters.mAndA) return false;
    }

    return true;
  });
}
